/* eslint-disable camelcase */
import {
  Alert,
  Button,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  StyledReactSelect,
} from '@fountain/fountain-ui-components';
import { Typography } from '@material-ui/core';
import {
  DocumentSignatureStagesTemplateSetting,
  DocumentSigningStagesTemplateSetting,
  EmbeddedSignatureTemplate,
  OfferLetterTemplateForStage,
  OfferLetterTemplatesStage,
  User,
  WorkflowStageDetail,
} from 'api-clients/monolith';
import produce from 'immer';
import React, { FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

import {
  DocumentSigningRuleProps,
  OtherSetSignerMappings,
} from 'containers/WorkflowEditor/components/Rules/DocumentSigningRules/types';
import globalMessages from 'shared/global/messages';

import { messages } from './messages';
import { useStyles } from './styles';

export interface DocumentModalProps {
  onClose: () => void;
  availableTemplates: Array<EmbeddedSignatureTemplate>;
  existingTemplate?:
    | DocumentSignatureStagesTemplateSetting
    | DocumentSigningStagesTemplateSetting;
  embeddedSignatureTemplateStages?: Array<{
    id?: number;
    stage_id?: number;
    signers_mapping?: Record<string, string>;
    embedded_signature_template_id?: number;
  }>;
  accountUsers?: User[];
  setStage: React.Dispatch<React.SetStateAction<WorkflowStageDetail>>;
  setDocumentSigningRules?: React.Dispatch<
    React.SetStateAction<DocumentSigningRuleProps>
  >;
  ruleId?: string | number;
  /**
   * Offer letter templates the stage can attach (AXHE-4106). Offered in the
   * same picker as the legacy documents so there is ONE "Add document" flow,
   * not two — the whole point of merging the lists.
   *
   * Passed for the stage's default set and for an Alternate Documents rule set
   * alike (AXHE-4608). Which of the two a submit writes to is decided by
   * `setDocumentSigningRules`, exactly as it is for a legacy document.
   */
  availableOfferLetterTemplates?: Array<OfferLetterTemplateForStage>;
  /**
   * What the OTHER rule sets on this stage already assign, by template id
   * (AXHE-4608) — the modal refuses a mapping that disagrees with one of them.
   * See `OtherSetSignerMappings`.
   */
  otherSetSignerMappings?: OtherSetSignerMappings;
  /** Already attached, so not offered again — except the one being edited. */
  assignedOfferLetterTemplateIds?: number[];
  /**
   * The offer letter template this modal is EDITING, if any (AXHE-4446).
   * Mutually exclusive with `existingTemplate`, which carries the legacy shape.
   *
   * Its presence is also what puts the picker into single-kind mode: see
   * `editingKind` below.
   */
  existingOfferLetterTemplateId?: number;
  /**
   * The stage's offer letter assignments, so an existing signers_mapping can be
   * loaded and edited. The offer-letter twin of
   * `embeddedSignatureTemplateStages`.
   */
  offerLetterTemplatesStages?: Array<OfferLetterTemplatesStage>;
  /** Keys the offer letter template editor route this modal links out to. */
  accountSlug?: string;
}

// A picker entry, normalized across the two template kinds so react-select can
// list them together. They live in different tables, so ids can collide — the
// option value is namespaced by kind rather than being the bare id.
type DocumentOption =
  | {
      kind: 'legacy';
      id: number;
      title: string;
      template: EmbeddedSignatureTemplate;
    }
  | {
      kind: 'offer_letter';
      id: number;
      title: string;
      template: OfferLetterTemplateForStage;
    };

/**
 * A role that needs a person assigned to it at this stage, normalized across the
 * two template kinds (AXHE-4446).
 *
 * `key` is what the persisted signers_mapping is keyed by, and the two kinds
 * disagree about what that is: a legacy embedded template maps by the role's
 * NAME, an offer letter template by its roster key (a `slug()`ed identifier). So
 * key and label are the same string for a legacy role and deliberately different
 * for an offer letter one — never collapse them.
 */
interface SignerRole {
  key: string;
  label: string;
}

/**
 * Drop mapping entries whose role the template no longer declares.
 *
 * A mapping saved before someone edited the template's roster can carry a key
 * that is gone. Writing it back is rejected — OfferLetterTemplatesStage's
 * `unknown_signer_keys?` validation fails the whole stage save — and until then
 * the orphan's email counts towards the duplicate-assignee check, so a stale
 * entry can block the form over a role that isn't even shown.
 */
const forRoles = (
  mapping: Record<string, string> | undefined,
  roles: SignerRole[],
): Record<string, string> =>
  Object.fromEntries(
    roles
      .map(role => [role.key, mapping?.[role.key]] as const)
      .filter((entry): entry is readonly [string, string] => Boolean(entry[1])),
  );

/**
 * Two mappings are the same assignment when they name the same people for the
 * same roles.
 *
 * Compared the way OfferLetterTemplatesStage#normalized_signers_mapping compares
 * them server-side — role keys lower-cased, emails trimmed and lower-cased — so
 * the editor and the save agree on what "different" means. Comparing raw values
 * would refuse an assignment over capitalization.
 */
const sameAssignment = (
  left: Record<string, string>,
  right: Record<string, string>,
): boolean => {
  const normalize = (mapping: Record<string, string>) =>
    Object.entries(mapping)
      .map(
        ([key, email]) =>
          `${key.toLowerCase()}=${String(email).trim().toLowerCase()}`,
      )
      .sort()
      .join('|');

  return normalize(left) === normalize(right);
};

export const DocumentModal: FC<DocumentModalProps> = ({
  onClose,
  availableTemplates,
  existingTemplate,
  embeddedSignatureTemplateStages,
  accountUsers,
  setStage,
  setDocumentSigningRules,
  ruleId,
  availableOfferLetterTemplates,
  assignedOfferLetterTemplateIds,
  existingOfferLetterTemplateId,
  offerLetterTemplatesStages,
  otherSetSignerMappings,
  accountSlug,
}) => {
  const intl = useIntl();
  const styles = useStyles();
  const isEditing =
    existingOfferLetterTemplateId != null ||
    typeof existingTemplate?.id !== 'string';

  // An offer letter template chosen in the picker. Mutually exclusive with
  // `selectedDocument` — one submit writes one kind.
  const [selectedOfferLetterTemplateId, setSelectedOfferLetterTemplateId] =
    useState<number | null>(existingOfferLetterTemplateId ?? null);

  const [selectedDocument, setSelectedDocument] = useState(
    availableTemplates.find(
      temp =>
        existingTemplate?.template_id ===
        (temp.document_signature_template_id ?? temp.id),
    ),
  );

  const selectedOfferLetterTemplate = (
    availableOfferLetterTemplates ?? []
  ).find(template => template.id === selectedOfferLetterTemplateId);

  // The roles the SELECTED template needs a person for, whichever kind it is.
  //
  // A legacy template's roles come off the HelloSign template and are keyed by
  // name, with 'Applicant' filtered out. An offer letter template's come off its
  // roster, already candidate-free — OfferLetterTemplate#countersigner_roles
  // partitions on the `c` marker rather than on a name, because the candidate
  // role is customer-named and 'Applicant' is only its default.
  const signerRoles: SignerRole[] = selectedOfferLetterTemplate
    ? (selectedOfferLetterTemplate.countersigner_roles ?? []).map(role => ({
        key: role.key ?? '',
        label: role.label ?? role.key ?? '',
      }))
    : (selectedDocument?.signer_roles ?? [])
        .filter(role => role.name !== 'Applicant')
        .map(role => ({ key: role.name ?? '', label: role.name ?? '' }));

  const offerLetterDefaultSigners = offerLetterTemplatesStages?.find(
    assignment =>
      assignment.offer_letter_template_id === existingOfferLetterTemplateId,
  )?.signers_mapping;

  const defaultSigners =
    existingOfferLetterTemplateId != null
      ? offerLetterDefaultSigners
      : embeddedSignatureTemplateStages?.find(
          ests =>
            ests.embedded_signature_template_id ===
            existingTemplate?.template_id,
        )?.signers_mapping;

  const [signers, setSigners] = useState(defaultSigners);

  // Both validations read the mapping through `forRoles`, i.e. the roles that
  // are actually on screen — the same view `handleSubmit` writes.
  //
  // Checking raw `signers` instead let an ORPHAN block the form (germs12 review
  // on #4067). `signers` is seeded from the persisted mapping, which can carry a
  // role key the roster no longer declares; if that orphan's email matched
  // someone legitimately assigned, the duplicate alert fired and the submit went
  // disabled with no offending field on screen to clear — a dead end with no way
  // out of the modal. Pruning at write time alone never reached the check.
  const signerEmails = Object.values(forRoles(signers, signerRoles));
  const nonUniqueSigners = signerEmails.filter(
    (email, idx) => signerEmails.indexOf(email) !== idx,
  );

  const unassignedRoles = signerRoles.filter(role => !signers?.[role.key]);

  // Another rule already countersigns this template, and differently. An
  // applicant who matches both rules has no answer at send time, so the save is
  // refused here rather than at the first send (AXHE-4608).
  const conflictsWithAnotherSet =
    selectedOfferLetterTemplateId != null &&
    (otherSetSignerMappings?.[selectedOfferLetterTemplateId] ?? []).some(
      mapping => !sameAssignment(mapping, forRoles(signers, signerRoles)),
    );

  /* eslint-disable no-underscore-dangle */
  const handleSubmit = () => {
    // What both kinds persist, and the same view of the mapping the validations
    // above read. Hoisted out of the two branches so `forRoles` is the ONE
    // normalization point: a check that prunes while a write does not would swap
    // the disabled-button dead end for a 422 from the join's own duplicate
    // validation instead of fixing it.
    const signersMapping = forRoles(signers, signerRoles);

    // An offer letter template writes ONLY its join row — the legacy template
    // set and embedded-signature stages below are legacy-shaped and have no
    // meaning for it. What it DOES share is the signer mapping: the template's
    // roster names the roles, and this stage decides who fills them, exactly as
    // a legacy embedded template does. The template's content and merge
    // configuration stay in the offer letter editor.
    //
    // AXHE-4446: writing `signers_mapping` here is what makes such a template
    // sendable at all. OfferLetters::SignerBuilder resolves each countersigner
    // through this column and raises UnresolvableSignersError when it is blank,
    // and until now nothing wrote it — so every template with an employer
    // countersigner failed at send.
    // An Alternate Documents set writes into the RULE, not the stage
    // (AXHE-4608). The attachment rides inside the rule's own document set,
    // which is the only shape a rule created in this same editing session can
    // use — its set has no id yet for a stage-level attachment to name.
    if (selectedOfferLetterTemplateId != null && setDocumentSigningRules) {
      setDocumentSigningRules(
        produce((draftRules: DocumentSigningRuleProps) => {
          if (!ruleId) return;

          const templateSet = (draftRules[ruleId] || {})
            .template_set_attributes;
          if (!templateSet) return;

          const attrs =
            templateSet.offer_letter_templates_stages_attributes ?? [];

          const editing =
            existingOfferLetterTemplateId != null
              ? attrs.find(
                  assignment =>
                    !assignment._destroy &&
                    assignment.offer_letter_template_id ===
                      existingOfferLetterTemplateId,
                )
              : undefined;

          // Re-attaching one detached earlier in this session resurrects that
          // row rather than adding a second: (template, set) is unique in the
          // database, and Rails validates the re-pointed row before destroying
          // the marked one.
          const marked = attrs.find(
            assignment =>
              assignment._destroy &&
              assignment.offer_letter_template_id ===
                selectedOfferLetterTemplateId,
          );

          if (editing && marked && editing !== marked) {
            delete marked._destroy;
            marked.signers_mapping = signersMapping;
            if (editing.id != null) {
              editing._destroy = true;
            } else {
              attrs.splice(attrs.indexOf(editing), 1);
            }
          } else if (editing) {
            editing.offer_letter_template_id = selectedOfferLetterTemplateId;
            editing.signers_mapping = signersMapping;
          } else if (marked) {
            delete marked._destroy;
            marked.signers_mapping = signersMapping;
          } else {
            attrs.push({
              offer_letter_template_id: selectedOfferLetterTemplateId,
              signers_mapping: signersMapping,
            });
          }

          templateSet.offer_letter_templates_stages_attributes = attrs;
        }),
      );
      onClose();
      return;
    }

    if (selectedOfferLetterTemplateId != null) {
      setStage(
        produce((draftStage: WorkflowStageDetail) => {
          if (draftStage.type !== 'DocumentSigningStage') return;

          const attrs =
            draftStage.additional_info
              .offer_letter_templates_stages_attributes ?? [];

          // Editing an existing attachment: update the row in place rather than
          // detaching and re-adding. That keeps its `id`, so a persisted row is
          // UPDATEd, and it also covers the admin swapping which template is
          // attached — `offer_letter_template_id` is permitted on the nested
          // attributes and re-pointing it re-runs the tenancy validation
          // (OfferLetterTemplatesStage, `if: :offer_letter_template_id_changed?`).
          const editing =
            existingOfferLetterTemplateId != null
              ? attrs.find(
                  assignment =>
                    !assignment._destroy &&
                    assignment.offer_letter_template_id ===
                      existingOfferLetterTemplateId,
                )
              : undefined;

          if (editing) {
            // Swapping onto a template that was DETACHED earlier in this same
            // unsaved session (fountain-reef on #4067). Its row is still in the
            // payload carrying `_destroy`, and still in the database — Rails
            // validates the re-pointed row BEFORE destroying the marked one, so
            // `validates :stage_id, uniqueness: { scope: :offer_letter_template_id }`
            // sees the duplicate and 422s the whole stage save with "stage has
            // already been taken". Verified against the real join rather than
            // reasoned about.
            //
            // Resurrect that row and destroy this one instead, so nothing is
            // ever re-pointed onto an occupied (template, stage) pair. Reef's
            // suggested fix — dropping the marked row from the payload — does
            // NOT work: an omitted row is left alone by nested attributes, so
            // the duplicate survives in the database and the save still 422s.
            const markedTarget = attrs.find(
              assignment =>
                assignment._destroy &&
                assignment.offer_letter_template_id ===
                  selectedOfferLetterTemplateId,
            );

            if (markedTarget) {
              delete markedTarget._destroy;
              markedTarget.signers_mapping = signersMapping;
              // Same rule as handleDetachOfferLetterTemplate: a persisted row
              // needs `_destroy` to be removed server-side, one added this
              // session has no id and can just be dropped.
              if (editing.id != null) {
                editing._destroy = true;
              } else {
                attrs.splice(attrs.indexOf(editing), 1);
              }
            } else {
              editing.offer_letter_template_id = selectedOfferLetterTemplateId;
              editing.signers_mapping = signersMapping;
            }

            draftStage.additional_info.offer_letter_templates_stages_attributes =
              attrs;
            return;
          }

          // Re-attaching one detached earlier in this session resurrects the
          // original row rather than adding a duplicate — (template, stage) is
          // unique in the DB.
          const marked = attrs.find(
            assignment =>
              assignment._destroy &&
              assignment.offer_letter_template_id ===
                selectedOfferLetterTemplateId,
          );
          if (marked) {
            delete marked._destroy;
            marked.signers_mapping = signersMapping;
          } else {
            attrs.push({
              offer_letter_template_id: selectedOfferLetterTemplateId,
              signers_mapping: signersMapping,
            });
          }
          draftStage.additional_info.offer_letter_templates_stages_attributes =
            attrs;
        }),
      );
      onClose();
      return;
    }

    const newTemplateId =
      selectedDocument?.document_signature_template_id ?? selectedDocument?.id;

    if (!setDocumentSigningRules) {
      setStage(
        produce((draftStage: WorkflowStageDetail) => {
          if (
            (draftStage.type !== 'DocumentSignatureStage' &&
              draftStage.type !== 'DocumentSigningStage') ||
            !selectedDocument
          ) {
            return;
          }

          const elseTemplateSet =
            draftStage.additional_info?.else_template_set_attributes;
          if (elseTemplateSet?.template_settings_attributes) {
            // TemplateSet exists
            if (isEditing) {
              // Editing existing template
              const draftTemplate =
                elseTemplateSet.template_settings_attributes.find(
                  temp =>
                    temp.template_id ===
                    (existingTemplate?.document_signature_template_id ??
                      existingTemplate?.template_id),
                );
              if (draftTemplate) {
                draftTemplate.template_id = newTemplateId;
                draftTemplate.embedded_signature_template_title =
                  selectedDocument.title;
              }
            } else {
              // Adding new template to list
              elseTemplateSet.template_settings_attributes.push({
                template_id: newTemplateId,
                embedded_signature_template_title: selectedDocument.title,
              });
              // Note: the corollary to this lives in <Documents> `handleDeleteDocument` where we
              // destroy the entire template set if the last template is deleted
              if (elseTemplateSet._destroy) {
                delete elseTemplateSet._destroy;
              }
            }
          } else {
            // Net New
            draftStage.additional_info = {
              ...draftStage.additional_info,
              else_template_set_attributes: {
                id: uuid(),
                template_settings_attributes: [
                  {
                    template_id: newTemplateId,
                    embedded_signature_template_title: selectedDocument.title,
                  },
                ],
              },
            };
          }
        }),
      );
    } else {
      setDocumentSigningRules(
        produce((draftRules: DocumentSigningRuleProps) => {
          if (!selectedDocument || !ruleId) return;

          const draftTemplateSettings =
            ((draftRules[ruleId] || {}).template_set_attributes ?? {})
              .template_settings_attributes ?? [];

          if (isEditing) {
            // Editing existing template
            const draftTemplate = draftTemplateSettings.find(
              temp =>
                temp.template_id ===
                (existingTemplate?.document_signature_template_id ??
                  existingTemplate?.template_id),
            );
            if (draftTemplate) {
              draftTemplate.template_id = newTemplateId;
              draftTemplate.embedded_signature_template_title =
                selectedDocument.title;
            }
          } else {
            draftTemplateSettings.push({
              template_id: newTemplateId,
              embedded_signature_template_title: selectedDocument.title,
            });
          }
        }),
      );
    }
    setStage(
      produce((draftStage: WorkflowStageDetail) => {
        if (
          draftStage.type !== 'DocumentSignatureStage' &&
          draftStage.type !== 'DocumentSigningStage'
        ) {
          return;
        }
        // Signers only set manually for DocumentSigningStages
        if (draftStage.type !== 'DocumentSignatureStage') {
          const embeddedSignatureTemplatesStages =
            draftStage.additional_info
              .embedded_signature_templates_stages_attributes;

          if (embeddedSignatureTemplatesStages?.length) {
            // Existing Signers. The stage holds one entry per template, shared by
            // the Default set and every rule set that holds it. A stage saved
            // before that held can carry several, and each takes the new signers
            // so the backend's merge keeps them. The entry of the template being
            // replaced stays: another set may still hold it, and the backend drops
            // it once none does.
            const draftEmbeddedSignatureTemplatesStages =
              embeddedSignatureTemplatesStages.filter(
                ests =>
                  ests.embedded_signature_template_id === selectedDocument?.id,
              );

            if (draftEmbeddedSignatureTemplatesStages.length) {
              for (const draftEmbeddedSignatureTemplatesStage of draftEmbeddedSignatureTemplatesStages) {
                delete draftEmbeddedSignatureTemplatesStage._destroy;
                draftEmbeddedSignatureTemplatesStage.signers_mapping =
                  signersMapping;
              }
            } else {
              embeddedSignatureTemplatesStages.push({
                embedded_signature_template_id: selectedDocument?.id,
                stage_id: draftStage.id,
                signers_mapping: signersMapping,
              });
            }
          } else {
            // Net New Signer
            draftStage.additional_info = {
              ...draftStage.additional_info,
              embedded_signature_templates_stages_attributes: [
                {
                  embedded_signature_template_id: selectedDocument?.id,
                  stage_id: draftStage.id,
                  signers_mapping: signersMapping,
                },
              ],
            };
          }
        }
      }),
    );
    /* eslint-enable no-underscore-dangle */

    onClose();
  };

  const onChangeDocumentTemplate = (temp: EmbeddedSignatureTemplate) => {
    setSelectedDocument(temp);

    const newDefaultSigners = embeddedSignatureTemplateStages?.find(
      ests => ests.embedded_signature_template_id === temp?.id,
    )?.signers_mapping;

    if (newDefaultSigners) {
      setSigners(newDefaultSigners);
    } else if (
      temp.document_signature_template_id ||
      temp.id !== selectedDocument?.id
    ) {
      setSigners({});
    } else {
      setSigners(defaultSigners);
    }
  };

  const onChangeSignerRole = (signerRole: SignerRole) => (option: User) => {
    setSigners({
      ...signers,
      [signerRole.key]: option.email,
    });
  };

  const isSelectedDocumentUsedInStage = Boolean(
    embeddedSignatureTemplateStages?.find(
      ests => ests.embedded_signature_template_id === selectedDocument?.id,
    ),
  );

  // Which kind this modal is editing, or null when adding. In edit mode the
  // picker offers that kind ALONE (AXHE-4446).
  //
  // Not a restriction for its own sake: the two kinds persist to different
  // places, and the submit paths above each edit a row of their own kind. Cross
  // the kinds mid-edit and neither path can express it — picking a legacy
  // document while editing an offer letter attachment fell through to the legacy
  // branch, which pushed a NEW template setting and left the offer letter row
  // attached, so the stage ended up holding both. That hole has existed in the
  // other direction since AXHE-4106 put the two kinds in one picker. Swapping
  // kinds is remove-then-add, which both paths already handle.
  const editingKind: 'legacy' | 'offer_letter' | null = !isEditing
    ? null
    : existingOfferLetterTemplateId != null
      ? 'offer_letter'
      : 'legacy';

  const offerLetterOptions: DocumentOption[] =
    editingKind === 'legacy'
      ? []
      : (availableOfferLetterTemplates ?? [])
          // A deactivated template must never be newly attached (AXHE-3836), and
          // an already-attached one isn't offered twice — except the one being
          // edited, which has to stay selectable to be the current value.
          .filter(
            template =>
              template.id != null &&
              (template.active !== false ||
                template.id === existingOfferLetterTemplateId) &&
              (!(assignedOfferLetterTemplateIds ?? []).includes(template.id) ||
                template.id === existingOfferLetterTemplateId),
          )
          .map(template => ({
            kind: 'offer_letter' as const,
            id: template.id as number,
            title: template.title ?? '',
            template,
          }));

  const legacyOptions: DocumentOption[] =
    editingKind === 'offer_letter'
      ? []
      : availableTemplates.map(template => ({
          kind: 'legacy' as const,
          id: template.id ?? 0,
          title: template.title ?? '',
          template,
        }));

  // Grouped only when there is something in both groups — a stage with no offer
  // letter templates should look exactly as it did before this merge, and an
  // edit restricted to one kind needs no group headings at all.
  const documentOptions =
    offerLetterOptions.length > 0 && legacyOptions.length > 0
      ? [
          {
            label: intl.formatMessage(messages.documentsGroup),
            options: legacyOptions,
          },
          {
            label: intl.formatMessage(messages.offerLetterTemplatesGroup),
            options: offerLetterOptions,
          },
        ]
      : [...legacyOptions, ...offerLetterOptions];

  const selectedOption: DocumentOption | null =
    selectedOfferLetterTemplateId != null
      ? (offerLetterOptions.find(
          option => option.id === selectedOfferLetterTemplateId,
        ) ?? null)
      : (legacyOptions.find(option => option.id === selectedDocument?.id) ??
        null);

  const onChangeDocumentOption = (option: DocumentOption) => {
    if (option.kind === 'offer_letter') {
      setSelectedOfferLetterTemplateId(option.id);
      // Drop any legacy selection so the two can't both be submitted.
      setSelectedDocument(undefined);
      // Whoever is already assigned on this stage for THIS template, so
      // reopening an attachment shows its assignments rather than blank fields.
      // Selecting a different template starts empty: role keys are per-template,
      // so the previous template's mapping means nothing here.
      setSigners(
        offerLetterTemplatesStages?.find(
          assignment => assignment.offer_letter_template_id === option.id,
        )?.signers_mapping ?? {},
      );
      return;
    }
    setSelectedOfferLetterTemplateId(null);
    onChangeDocumentTemplate(option.template);
  };

  return (
    <Modal
      data-testid="document-modal"
      ariaLabelledBy="document-modal-header"
      open
      onClose={onClose}
      fullScreenOnMobile
      maxWidth={480}
    >
      <form onSubmit={handleSubmit} key={existingTemplate?.id}>
        <ModalHeader
          onClose={onClose}
          ariaLabelledBy="document-modal-header"
          showIcon={false}
        >
          {isEditing ? (
            <FormattedMessage {...messages.editDocument} />
          ) : (
            <FormattedMessage {...messages.addDocument} />
          )}
        </ModalHeader>
        <ModalContent>
          {nonUniqueSigners.length > 0 && (
            <Alert type="danger" className={styles.alertContainer} fullWidth>
              <FormattedMessage {...messages.uniqueSigners} />
            </Alert>
          )}
          {conflictsWithAnotherSet && (
            <Alert type="danger" className={styles.alertContainer} fullWidth>
              <FormattedMessage {...messages.conflictingSetSigners} />
            </Alert>
          )}
          <div>
            <StyledReactSelect
              aria-label={intl.formatMessage(messages.documentTemplate)}
              label={intl.formatMessage(messages.documentTemplate)}
              options={documentOptions}
              getOptionLabel={(option: DocumentOption) => option.title}
              // Namespaced: the two kinds come from different tables and their
              // ids can collide.
              getOptionValue={(option: DocumentOption) =>
                `${option.kind}-${option.id}`
              }
              onChange={onChangeDocumentOption}
              value={selectedOption}
            />
          </div>
          {/* The template's own content and its roster of roles are shared by
              every stage using it, so they are edited over in the offer letter
              editor — not here, where the ATTACHMENT is configured. Secondary to
              the assignment fields below, and deliberately not on the row's
              pencil, which now opens this modal (AXHE-4446). */}
          {selectedOfferLetterTemplate?.external_id && accountSlug && (
            <Link
              className={styles.templateEditorLink}
              to={`/${accountSlug}/offer_letter_templates/${selectedOfferLetterTemplate.external_id}`}
            >
              <FormattedMessage {...messages.editOfferLetterTemplate} />
            </Link>
          )}
          {(selectedDocument || selectedOfferLetterTemplate) &&
            accountUsers &&
            signerRoles.map(signerRole => {
              return (
                <div
                  className={styles.signerField}
                  key={`${selectedOfferLetterTemplate ? 'offer_letter' : 'legacy'}-${
                    selectedOfferLetterTemplate?.id ??
                    selectedDocument?.template_id ??
                    ''
                  }-${signerRole.key}`}
                >
                  <StyledReactSelect
                    aria-label={intl.formatMessage(messages.roleSelectLabel, {
                      role: signerRole.label,
                    })}
                    label={intl.formatMessage(messages.roleSelectLabel, {
                      role: signerRole.label,
                    })}
                    options={accountUsers}
                    getOptionLabel={(user: User) => user.name}
                    getOptionValue={(user: User) => user.email}
                    onChange={onChangeSignerRole(signerRole)}
                    value={accountUsers.find(
                      option => option.email === signers?.[signerRole.key],
                    )}
                    error={nonUniqueSigners.some(
                      email => email === signers?.[signerRole.key],
                    )}
                  />
                </div>
              );
            })}

          {/* Saveable as it stands — see `incompleteSigners`. The send is what
              refuses an unassigned role, so this says so rather than blocking a
              save the backend deliberately allows. */}
          {selectedOfferLetterTemplate && unassignedRoles.length > 0 && (
            <Alert type="warning" className={styles.warningAlert} fullWidth>
              <FormattedMessage {...messages.incompleteSigners} />
            </Alert>
          )}

          {isSelectedDocumentUsedInStage && (
            <Typography
              variant="body2"
              color="textPrimary"
              className={styles.warning}
            >
              <FormattedMessage {...messages.warningChangesToRoles} />
            </Typography>
          )}
        </ModalContent>
        <ModalFooter>
          <Button
            className={styles.button}
            disableRipple
            onClick={onClose}
            type="secondary"
          >
            <FormattedMessage {...globalMessages.cancel} />
          </Button>
          <Button
            className={styles.button}
            data-testid="document-modal-submit-button"
            disableRipple
            disabled={
              // Duplicates block BOTH kinds: two roles mapped to one person is
              // rejected server-side (OfferLetterTemplatesStage and
              // EmbeddedSignatureTemplatesStage alike), and at send time it hands
              // the signing provider two signer entries for the same human.
              //
              // An INCOMPLETE mapping blocks only the legacy kind, which is the
              // asymmetry it looks like. Attaching an offer letter template
              // before its countersigner is known is a supported flow — the join
              // validates shape only and says so explicitly, because AXHE-3754
              // assigns templates to stages before emails are filled in. So an
              // unassigned role warns (`incompleteSigners`) and still saves,
              // rather than making the stage unsaveable.
              nonUniqueSigners.length > 0 ||
              conflictsWithAnotherSet ||
              (selectedOfferLetterTemplateId != null
                ? false
                : !selectedDocument || unassignedRoles.length > 0)
            }
            submit
          >
            {isEditing ? (
              <FormattedMessage {...messages.updateDocument} />
            ) : (
              <FormattedMessage {...messages.addDocument} />
            )}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};
